import { Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { accessScopeQuery } from "../../../api/session";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../../platform/jobs/detail/GenerationDetailModal";
import { useJobList } from "../../../platform/jobs/job-list";
import { modelTitle } from "../../../platform/jobs/model-name";
import { useStreamStatus } from "../../../platform/stream-status";
import home from "../home2.module.css";
import { ResultCard } from "../ResultCard";
import classes from "./simple.module.css";

/** How many of the person's results show. */
const SHOWN = 6;
/** The brand's jobs read to find them: the team's, newest first, the person's kept. */
const READ = 24;

/**
 * "Your recent results": the person's own newest jobs in the brand, wherever they were started (a
 * job records no UI; spec ui-perf/simple-studio, decision 5). One opens the Studio's own detail.
 */
export function SimpleRecent({ teamId, brandId }: { teamId: string; brandId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const userId = useQuery({ ...accessScopeQuery(), select: (scope) => scope.user_id }).data;
  const jobs = useJobList(teamId, { status: "all", brandId, pageSize: READ }, useStreamStatus());
  const catalog = useGetCatalog().data;
  const mine = jobs.data?.pages
    .flatMap((page) => page.items)
    .filter((job) => job.created_by === userId)
    .slice(0, SHOWN);
  return (
    <section className={classes.step} aria-labelledby="simple-recent">
      <div className={classes.resultHead}>
        <h2 id="simple-recent" className={classes.stepTitle}>
          Your recent results
        </h2>
        <span className={classes.grow} />
        <Link
          to="/teams/$teamId/brands/$brandId/home2/generations"
          params={{ teamId, brandId }}
          className={home.subtle}
        >
          All generations <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {mine === undefined ? (
        jobs.isError ? null : (
          <div className={classes.recent}>
            {[0, 1, 2].map((each) => (
              <Skeleton key={each} className={home.mediaSkeleton} />
            ))}
          </div>
        )
      ) : mine.length === 0 ? (
        <p className={classes.muted}>What you make shows up here, and in Generations.</p>
      ) : (
        <div className={classes.recent}>
          {mine.map((job) => (
            <ResultCard
              key={job.id}
              job={job}
              model={modelTitle(catalog, job)}
              onLost={() => void jobs.refetch()}
              onOpen={() => setOpen(job.id)}
            />
          ))}
        </div>
      )}
      {open === null ? null : (
        <GenerationDetailModal teamId={teamId} jobId={open} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}
